import { memo, useEffect, useMemo, useState } from 'react'
import { MessageSquare, StickyNote } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'
import { useMessageEditor } from '@/hooks/inbox/useMessageEditor'
import ChatHeader from './ChatHeader'
import MessageTimeline from './MessageTimeline'
import MessageInputArea from './MessageInputArea'
import NoteInputArea from './NoteInputArea'
import OnlineIndicator from './OnlineIndicator'
import { useInboxContext } from './InboxContext'
import { getInitials, getAvatarColor, isUserOnline } from './utils'

export default memo(function ChatPanel() {
  // Force re-render every 60s to update relative timestamps.
  // This is local to ChatPanel so it doesn't propagate to unrelated components.
  const [, setTimeTick] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => setTimeTick((t) => t + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  const {
    conversation,
    messages,
    internalNotes,
    currentAgent,
    typingUsers,
    messagesEndRef,
    messagesContainerRef,
    onRetryMessage,
    onRetryNote,
    onDeleteMessage,
    setReplyingToMessage,
    lightbox,
    onVideoClick,
  } = useInboxContext()

  const [activeTab, setActiveTab] = useState<'messages' | 'notes'>('messages')

  // Message editing
  const {
    editingMessageId,
    editingBody,
    setEditingBody,
    hasEditingText,
    isSavingEdit,
    deletingMessage,
    setDeletingMessage,
    canEditMessage,
    startEditingMessage,
    cancelEditingMessage,
    saveEditedMessage,
    handleEditingTextChange,
  } = useMessageEditor({
    conversationId: conversation.id,
    messages,
    currentAgentId: currentAgent.id,
  })

  // Computed timeline values
  const hasMultipleAgents = useMemo(() => {
    const agentIds = new Set(
      messages
        .filter(m => m.author_type === 'Agent' && m.author?.id)
        .map(m => m.author.id)
    )
    return agentIds.size > 1
  }, [messages])

  const agentFirstMessageIds = useMemo(() => {
    const seen = new Set<number>()
    const firstIds = new Set<number>()
    for (const m of messages) {
      if (m.author_type === 'Agent' && m.author?.id && !seen.has(m.author.id)) {
        seen.add(m.author.id)
        if (seen.size > 1) firstIds.add(m.id)
      }
    }
    return firstIds
  }, [messages])

  const timelineItems = useMemo(() => {
    return [
      ...messages.map((m) => ({ type: 'message' as const, data: m, created_at: m.created_at })),
      ...internalNotes.map((n) => ({ type: 'note' as const, data: n, created_at: n.created_at })),
    ].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  }, [messages, internalNotes])

  return (
    <>
      <div className="flex-1 flex flex-col bg-white">
        <ChatHeader />

        {/* Messages & Notes Timeline */}
        <div className="flex-1 overflow-y-auto p-4">
          <div ref={messagesContainerRef} className="max-w-4xl mx-auto space-y-3">
            <MessageTimeline
              timelineItems={timelineItems}
              conversation={conversation}
              hasMultipleAgents={hasMultipleAgents}
              agentFirstMessageIds={agentFirstMessageIds}
              editingMessageId={editingMessageId}
              editingBody={editingBody}
              hasEditingText={hasEditingText}
              isSavingEdit={isSavingEdit}
              onEditingBodyChange={setEditingBody}
              onEditingTextChange={handleEditingTextChange}
              onSaveEdit={saveEditedMessage}
              onCancelEdit={cancelEditingMessage}
              onStartEdit={startEditingMessage}
              canEditMessage={canEditMessage}
              onReply={setReplyingToMessage}
              onDeleteMessage={(messageId) => setDeletingMessage({ conversationId: conversation.id, messageId })}
              onRetryMessage={onRetryMessage}
              onRetryNote={onRetryNote}
              lightbox={lightbox}
              onVideoClick={onVideoClick}
            />

            {/* Typing Indicator */}
            {typingUsers
              .filter((u) => !(u.user_type === 'Agent' && u.user_id === currentAgent.id))
              .map((typingUser) => {
                const isAgentTyping = typingUser.user_type === 'Agent'
                return (
                  <div
                    key={typingUser.user_id}
                    className={`flex gap-2 max-w-4xl mx-auto ${isAgentTyping ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="flex-shrink-0 relative">
                      {isAgentTyping && typingUser.avatar_url ? (
                        <img
                          src={typingUser.avatar_url}
                          alt={typingUser.user_name}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium ${getAvatarColor(
                            isAgentTyping ? typingUser.user_name : (conversation.end_user.name || '')
                          )}`}
                        >
                          {getInitials(isAgentTyping ? typingUser.user_name : (conversation.end_user.name || ''))}
                        </div>
                      )}
                      {!isAgentTyping && isUserOnline(conversation.end_user.last_seen_at ?? null) && (
                        <OnlineIndicator size="sm" />
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-3 ${isAgentTyping ? 'bg-blue-100 rounded-br-md' : 'bg-gray-100 rounded-bl-md'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full animate-bounce bg-gray-500" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full animate-bounce bg-gray-500" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full animate-bounce bg-gray-500" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )
              })}

            <div className="h-[11px]" />
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area with Tab Switcher */}
        <div className="bg-white border-t border-gray-200 p-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3 mb-2">
              <button
                onClick={() => setActiveTab('messages')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                  activeTab === 'messages'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Responder
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                  activeTab === 'notes'
                    ? 'bg-amber-50 text-amber-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <StickyNote className="w-3.5 h-3.5" />
                Nota
              </button>
            </div>

            {activeTab === 'messages' ? <MessageInputArea /> : <NoteInputArea />}
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!deletingMessage}
        onOpenChange={(open) => !open && setDeletingMessage(null)}
        title="Excluir mensagem"
        description="Tem certeza que deseja excluir esta mensagem? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        variant="destructive"
        onConfirm={() => {
          if (deletingMessage) {
            onDeleteMessage(deletingMessage.conversationId, deletingMessage.messageId)
            setDeletingMessage(null)
          }
        }}
      />
    </>
  )
})
